import React from "react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import {
  BackgroundVariant,
  CircleButton,
} from "@sonder/ui/source/styled/fragments/CircleButton";
import { useSegment } from "analytics/segment";
import { useErrorReporterFiltered } from "core/hooks/useErrorReporterFiltered";
import { useUpdateTask } from "core/queries/UpdateTask";
import { addUndo, removeUndo } from "tasks/actions";
import { normalizeType } from "tasks/helpers";
import { useStore } from "tasks/store";
import { FilterStatus, TypeEnum } from "typings/globalGQLTypes";

const MAX_DESCRIPTION_LENGTH = 30;

const getUndoMessage = (description: string) => {
  const shortDescription =
    description.length > MAX_DESCRIPTION_LENGTH
      ? `${description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
      : description;
  return `Task "${shortDescription}" completed.`;
};

const isDisabled = (task: Task) => {
  const normalizedTaskType = task.type && normalizeType(task.type);
  // Conditions for Disabled:
  switch (normalizedTaskType) {
    // A Space Clean Task, once completed, cannot be uncompleted.
    case TypeEnum.Tom_Tasks_SpaceClean:
      return (
        task.status !== FilterStatus.ready &&
        task.status !== FilterStatus.inprogress
      );
    // A Clean Task cannot be completed if unscheduled.
    // A Clean Task, once completed, cannot be uncompleted.
    case TypeEnum.Tom_Tasks_Clean:
      return (
        !task.scheduledStartAt ||
        !task.dueBy ||
        (task.status !== FilterStatus.ready &&
          task.status !== FilterStatus.inprogress)
      );
    case TypeEnum.Tom_Tasks_QualityAudit:
      return true;
    default:
      return false;
  }
};

interface CompleteTaskButtonProps {
  backgroundVariant: BackgroundVariant;
  className?: string;
  description: string;
  eventName: string;
  task: Task;
}
export const CompleteTaskButton = ({
  backgroundVariant,
  className,
  description,
  eventName,
  task,
}: CompleteTaskButtonProps) => {
  const { id: taskId, status } = task;
  const { track } = useSegment();
  const { dispatch } = useStore();
  const errorReporter = useErrorReporterFiltered();
  const [onUpdateTask] = useUpdateTask({
    refetchQueries: ["GetTasks", "GetTask"],
  });
  const normalizedTaskType = task.type && normalizeType(task.type);
  return (
    <CircleButton
      backgroundVariant={backgroundVariant}
      className={className}
      disabled={isDisabled(task)}
      title="Complete task"
      onClick={async e => {
        e.stopPropagation();
        if (status) {
          const nextStatus =
            status === FilterStatus.complete
              ? FilterStatus.ready
              : FilterStatus.complete;
          try {
            await onUpdateTask({ variables: { taskId, status: nextStatus } });
            if (
              // You cannot undo Space Cleans / Clean Tasks anymore.
              normalizedTaskType !== TypeEnum.Tom_Tasks_SpaceClean &&
              normalizedTaskType !== TypeEnum.Tom_Tasks_Clean
            ) {
              if (nextStatus === FilterStatus.complete) {
                dispatch(addUndo(taskId, getUndoMessage(description)));
              } else {
                dispatch(removeUndo(taskId));
              }
            }
            track({
              eventName,
              action: "click",
              element: "complete_task_button",
              props: { nextValue: nextStatus },
            });
          } catch (error) {
            errorReporter.notify(error);
          }
        }
      }}
    >
      {status === FilterStatus.complete ? (
        <CheckCircleIcon data-testid="already-completed-icon" />
      ) : (
        <CheckCircleOutlineIcon data-testid="can-be-complete-icon" />
      )}
    </CircleButton>
  );
};
